// Upload history on the upload page, in two sections, Crashing and Box-outs (they are independent sheets). Each row is
// one stored sheet, labelled with its date, its event (a practice, or a game and its opponent) and whose numbers it
// holds (tracking / edited / uploaded), with Edit (reopen the review step from the stored CSV) and Delete (confirm in
// the row, then take that sheet's rows off the board). A game's crashing comes from tracking: it can't be deleted, and
// once a person edits it, Revert to tracking puts tracking's numbers back. A game nobody charted box-outs for shows in
// Box-outs as not charted, with Chart it. Replaced and deleted uploads stay in the record for audit; they're behind
// "show replaced / deleted" so the default view is what's on the board now.
import React, { useState } from "react";
import { Palette } from "./reboundingModel";
import { BoardGame, ReboundingPage, UploadRecord } from "./types";
import { head14, md, num, pill, RED, who, when } from "./ui";
import { canRevert, eventPhrase, eventText, isGame, ORIGIN_TEXT, originOf } from "./uploadLabels";

const COLS = "70px minmax(150px,190px) 110px 56px minmax(120px,1fr) 140px 170px 250px";

interface Props {
  P: Palette;
  uploads: UploadRecord[];
  games: BoardGame[];
  editingId: string | null;
  onEdit: (u: UploadRecord) => void;
  onDelete: (u: UploadRecord) => Promise<void>;
  onRevert: (u: UploadRecord) => Promise<void>;
  onChart: (g: BoardGame) => void;
}

type Line = { upload: UploadRecord; game?: undefined } | { upload?: undefined; game: BoardGame };

// Newest event first; within one event, the sheet on the board before what it replaced.
const byEvent = (a: Line, b: Line) => {
  const da = (a.upload ?? a.game).date;
  const db = (b.upload ?? b.game).date;
  if (da !== db) return da < db ? 1 : -1;
  return (b.upload?.uploadedAt ?? "").localeCompare(a.upload?.uploadedAt ?? "");
};

const UploadHistory: React.FC<Props> = ({ P, uploads, games, editingId, onEdit, onDelete, onRevert, onChart }) => {
  const [showAll, setShowAll] = useState(false);
  const [confirming, setConfirming] = useState<{ id: string; what: "delete" | "revert" } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const inactive = uploads.filter((u) => u.status !== "active").length;
  const shown = showAll ? uploads : uploads.filter((u) => u.status === "active");
  const charted = new Set(
    uploads.filter((u) => u.sheet === "boxouts" && u.status === "active").map((u) => u.sessionId),
  );

  const lines = (sheet: ReboundingPage): Line[] =>
    [
      ...shown.filter((u) => u.sheet === sheet).map((upload) => ({ upload })),
      ...(sheet === "boxouts" ? games.filter((g) => !charted.has(g.sessionId)).map((game) => ({ game })) : []),
    ].sort(byEvent);

  const status = (u: UploadRecord) =>
    u.status === "active"
      ? "ON THE BOARD"
      : `${u.status.toUpperCase()} · ${who(u.changedBy).toUpperCase()} · ${when(u.changedAt).toUpperCase()}`;

  const small: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: ".1em", color: P.sub };
  const event = (s: UploadRecord | BoardGame) => (
    <span
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 800,
        fontSize: 18,
        whiteSpace: "nowrap",
        color: isGame(s) ? RED : P.fg,
      }}
    >
      {eventText(s)}
    </span>
  );

  const run = async (u: UploadRecord, fn: (u: UploadRecord) => Promise<void>) => {
    setBusy(u.uploadId);
    try {
      await fn(u);
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  const confirmBar = (u: UploadRecord, what: "delete" | "revert") => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "0 0 10px",
        borderLeft: `6px solid ${what === "delete" ? P.bad : P.mid}`,
        paddingLeft: 12,
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: 17 }}>
        {what === "delete"
          ? `Remove ${u.rows} ${u.sheet === "crashing" ? "crashing" : "box-out"} rows from ${eventPhrase(u)}? The upload stays in the history, marked deleted.`
          : `Put tracking's numbers back for ${eventPhrase(u)}? The edited sheet stays in the history, marked replaced, and later imports of the game refresh it again.`}
      </span>
      <button
        onClick={() => run(u, what === "delete" ? onDelete : onRevert)}
        disabled={busy === u.uploadId}
        style={{
          ...pill(P, false, 14),
          height: 30,
          background: what === "delete" ? P.bad : P.mid,
          color: "#000",
          borderColor: what === "delete" ? P.bad : P.mid,
        }}
      >
        {busy === u.uploadId ? (what === "delete" ? "DELETING…" : "REVERTING…") : what === "delete" ? "DELETE" : "REVERT"}
      </button>
      <button onClick={() => setConfirming(null)} style={{ ...pill(P, false, 14), height: 30 }}>
        KEEP
      </button>
    </div>
  );

  const section = (sheet: ReboundingPage) => {
    const rows = lines(sheet);
    return (
      <section data-testid={`history-${sheet}`} style={{ marginTop: 18 }}>
        <div
          style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 26, lineHeight: 1, marginBottom: 4 }}
        >
          {sheet === "crashing" ? "CRASHING" : "BOX-OUTS"}
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: "max-content" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                columnGap: 14,
                height: 26,
                alignItems: "end",
                paddingBottom: 5,
                borderBottom: `2px solid ${P.headRule}`,
                ...head14(P),
              }}
            >
              <span>DATE</span>
              <span>EVENT</span>
              <span>SOURCE</span>
              <span style={{ textAlign: "right" }}>ROWS</span>
              <span>BY</span>
              <span>WHEN</span>
              <span>STATUS</span>
              <span />
            </div>
            {rows.map((line, i) => {
              const stripe = {
                borderBottom: `1px solid ${P.rule}`,
                background: i % 2 ? P.zebra : "transparent",
                margin: "0 -10px",
                padding: "0 10px",
              };
              const grid: React.CSSProperties = {
                display: "grid",
                gridTemplateColumns: COLS,
                columnGap: 14,
                minHeight: 44,
                alignItems: "center",
              };
              if (line.game) {
                const g = line.game;
                return (
                  <div key={`chart-${g.sessionId}`} data-testid="not-charted" style={stripe}>
                    <div style={grid}>
                      <span style={{ ...num, fontSize: 22 }}>{md(g.date)}</span>
                      {event(g)}
                      <span style={small}>NOT CHARTED</span>
                      <span />
                      <span />
                      <span />
                      <span style={small}>NOT ON THE BOARD</span>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button onClick={() => onChart(g)} style={{ ...pill(P, false, 13), height: 28 }}>
                          CHART IT
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }
              const u = line.upload;
              const active = u.status === "active";
              const origin = originOf(u);
              const asking = confirming?.id === u.uploadId ? confirming.what : null;
              const revertable = active && canRevert(u);
              const deletable = active && !(isGame(u) && u.sheet === "crashing");
              return (
                <div key={u.uploadId} data-testid="upload-row" style={{ ...stripe, opacity: active ? 1 : 0.5 }}>
                  <div style={grid}>
                    <span style={{ ...num, fontSize: 22 }}>{md(u.date)}</span>
                    {event(u)}
                    <span style={small}>{ORIGIN_TEXT[origin]}</span>
                    <span style={{ ...num, fontSize: 22, textAlign: "right" }}>{active ? u.rows : ""}</span>
                    <span
                      style={{ fontSize: 17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={u.uploadedBy ?? ""}
                    >
                      {origin === "tracking" ? "Tracking" : who(u.uploadedBy)}
                    </span>
                    <span style={{ fontSize: 17, color: P.sub }}>{when(u.uploadedAt)}</span>
                    <span style={small}>{status(u)}</span>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      {active && !asking && (
                        <>
                          <button
                            onClick={() => onEdit(u)}
                            disabled={editingId === u.uploadId}
                            style={{ ...pill(P, editingId === u.uploadId, 13), height: 28 }}
                          >
                            {editingId === u.uploadId ? "EDITING" : "EDIT"}
                          </button>
                          {revertable && (
                            <button
                              onClick={() => setConfirming({ id: u.uploadId, what: "revert" })}
                              style={{ ...pill(P, false, 13), height: 28 }}
                            >
                              REVERT TO TRACKING
                            </button>
                          )}
                          {deletable && (
                            <button
                              onClick={() => setConfirming({ id: u.uploadId, what: "delete" })}
                              style={{ ...pill(P, false, 13), height: 28 }}
                            >
                              DELETE
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {asking && confirmBar(u, asking)}
                </div>
              );
            })}
            {!rows.length && (
              <div style={{ padding: "12px 0", fontSize: 17, color: P.sub }}>
                No {sheet === "crashing" ? "crashing" : "box-out"} sheets yet.
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div style={{ marginTop: 34 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
        <span style={{ ...head14(P), color: RED, fontSize: 12, letterSpacing: ".14em" }}>UPLOAD HISTORY</span>
        {inactive > 0 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            style={{ ...pill(P, showAll, 12), height: 24, padding: "0 8px" }}
          >
            SHOW REPLACED / DELETED ({inactive})
          </button>
        )}
      </div>
      {section("crashing")}
      {section("boxouts")}
    </div>
  );
};

export default UploadHistory;
